import Backbone from 'backbone'
import _ from 'lodash'
import TooltipAndDetails from '../components/tooltip-and-details'
import $ from 'jquery'
import nonAbbreviatedValuesTooltip from './non-abbreviated-values-tooltip.hbs'
import globals from '../components/globals'
import i18n from '../components/i18n'
import treeIdsInFilters from '../datasets/tree-ids-in-filters'
import Dataset from '../datasets/model/dataset'
import DatasetsFilterBadges from '../datasets/filters/datasets-filter-badges'
import datasetValueTooltip from './overview/dataset-value-tooltip.hbs'
import deviceType from '../components/device-type'
import datasetSeriesDrilldown
	from '../datasets/overview/dataset-series-drilldown'
import appDisplay from '../components/app-display'
import DataUsedInCalculations from './overview/data-used-in-calculations'
import config from '../components/config'
import ScorecardNode from './scorecard-node'
import wrapModel from '../components/wrap-model'

const EquationValueTooltips = Backbone.View.extend(/** @lends {EquationValueTooltips} */{

	/**
	 * Tooltips on a table of metric values that show the equation used to
	 * calculate each value. Clicking on a value will open the
	 * DataUsedInCalculations dialog.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {jQuery} options.el the element containing all the values that
	 * need tooltips
	 *
	 * @param {Object[]|ScorecardNode[]} options.metrics the metrics whose
	 * values are being displayed
	 *
	 * @param {MetricChangeMetadata.kt[]} [options.proposedUpdates] proposed
	 * metric value updates to apply over the values saved in the database
	 */
	initialize: function(options = {}) {
		this.options = options
		this.metricsById = _.keyBy(this.options.metrics, 'id')
	},

	render: function() {
		this.setupEquationTooltips()
		return this
	},

	setupEquationTooltips: function() {
		_.invokeMap(this.equationTooltips, 'remove')

		// Create a new non-abbreviated tooltip for each value that needs it
		var elements = this.$('.text-link-emphasized')
		this.equationTooltips = []
		for (var i = 0; i < elements.length; i++) {
			const tooltip = new TooltipAndDetails({
				el: this.el,
				selector: $(elements[i]),
				tooltipOptions: {
					extraClass: 'tooltip-and-details non-abbreviated-tooltip scorecards-overview-values-non-abbreviated-tooltip equation-viewer '
				},
				tooltipContent: e => this.tooltipContent(e, tooltip),
				details: this.details.bind(this),
				clickable: () => elements[i].hasClass('has-equation')
			})

			this.equationTooltips.push(tooltip)
		}
	},

	getPeriodId: function(target) {
		// Try to get period ID from closest ancestor with data-period-id
		let periodId = this.$(target).closest('[data-period-id]').data('periodId')
		// If we don't find a period ID it means we're looking at one of the
		// use equation values, so use the global period ID
		return periodId || globals.periodId
	},

	getIndex: function(target) {
		const data = this.$(target).closest('[data-type]').data()
		return data.type === 'actual' ? 0 : data.index + 1
	},

	getSeriesKey: function(index) {
		return getSeriesKey(
			this.metric.get('scoringTypeId'),
			index
		)
	},

	tooltipContent: function(e, tooltip) {
		let snData = this.$(e.currentTarget)
			.closest('[data-scorecard-node-id]')
			.data()
		let periodData = this.$(e.currentTarget)
			.closest('[data-period-id]')
			.data()
		let data = _.extend({}, snData, periodData)

		const index = this.getIndex(e.target)
		this.metric = wrapModel(this.metricsById[data.scorecardNodeId], ScorecardNode)
		this.series = this.metric.get('series')[index]

		// if there's no equation, display a tooltip with the non-rounded value
		// (note calculate, rollup and dataset series all have equations)
		if (!data.unshortenedEquation) {
			if (data.unshortenedValue) {
				return nonAbbreviatedValuesTooltip({
					header: data.unshortenedHeader,
					value: data.unshortenedValue
				})
			} else {
				// If the unshortened value equals the shortened one, there's
				// no need to show any tooltip, so stop here.
				return
			}
		}

		if (this.series.updateType === 'dataset') {
			if (this.metric.get('replicated')) {
				return `<div class="tooltip-body-only"> ${i18n.get('common.replication.equationFromLowSideDataset')}</div>`
			}
			this.datasetSeries = JSON.parse(this.series.equation)
			var def = $.Deferred()
			let xhrData = { fields: true }
			xhrData.treeItemIds = treeIdsInFilters(this.datasetSeries.filters)
			if (!this.datasetSeriesXhr) {
				let url = `api/scorecards/${this.metric.id}/dataset-series`

				this.datasetSeriesXhr = $.ajax({
					method:'get',
					url: url,
					data: $.param(xhrData),
					error: (xhr) => {
						// #57105 resolve the $.Deferred() so that the
						// we don't see the loading dots
						def.resolve()
						tooltip.removeTooltip(e)
						// (subsequent hovers/clicks are handled below)
						if (xhr.state() == 'rejected') {
							// #56944 if the user does not have
							// permission to view the dataset,
							// hovering over the value does nothing
							return def
						}
					}
				})
			} else if (this.datasetSeriesXhr.state() == 'rejected') {
				// #56944 if the user does not have permission to
				// view the dataset, hovering over the value does
				// nothing
				return
			}
			this.datasetSeriesXhr.then(response => {
				let { datasets, treeItems, datasetSeries } = response
				this.metric.set('datasets', response)
				let key = this.metric.get('series')[index].key
				this.datasetSeries = datasetSeries[key]
				let datasetsById = _.keyBy(datasets, 'id')
				// #24122 in case the series refers to a view ID
				// instead of a root dataset ID, be sure to read the
				// dataset ID from this.datasetSeries
				let dataset = datasetsById[this.datasetSeries.id]
				let datasetModel = new Dataset(dataset)
				let datasetsFilterBadges = new DatasetsFilterBadges({
					model: datasetModel,
					filters: this.datasetSeries.filters,
					showPermissionFilters: true,
					noneMessage: i18n.get('common.thereAreNo', 'report.wizard.filters'),
					treeItemsById: _.keyBy(treeItems, 'id'),
					isEditable: false,
					clickable: false
				})

				let description = this.metric.getDatasetSeriesLabelForEquation(
					this.datasetSeries)
				let el = $(datasetValueTooltip({
					title: data.unshortenedHeader,
					showDetailsLink: deviceType.touchOnly,
					value: e.target.innerHTML != data.unshortenedValue ? data.unshortenedValue : null,
					description
				}))
				el.find('.filter-badges-container').append(datasetsFilterBadges.el)
				datasetsFilterBadges.render()
				def.resolve(el)
			})
			return def
		} else {
			// Only show the value if it was shortened
			var value
			if (e.target.innerText != data.unshortenedValue) {
				value = data.unshortenedValue
			}

			return nonAbbreviatedValuesTooltip({
				header: data.unshortenedHeader,
				value,
				equation: data.unshortenedEquation,
				equationHeader: i18n.get('scorecards.node.metric.equation'),
				showDetailsLink: deviceType.touchOnly
			})
		}
	},

	details: function(e) {
		if (this.series.updateType === 'dataset') {
			if (this.metric.get('replicated')) {
				return	//don't show anything if this is a replicated dataset metric
			}
			//#27763 abort the hover over req if drilled down
			_.invoke(this.datasetSeriesXhr, 'abort')
			datasetSeriesDrilldown(this.datasetSeries)
		} else {
			// #13670 fixes Blowup when clicking on values for non-calculated metric
			if (_.isEmpty(this.series.equation)) {
				return
			}

			const index = this.getIndex(e.target)
			const periodId = this.getPeriodId(e.target)
			const seriesKey = this.getSeriesKey(index)
			var url = 'api/scorecards/' + this.metric.id + '/data-used-in-calculations'

			let approvedUpdates = _.filter(
				this.options.proposedUpdates, 'approved')
			var xhr = $.ajax({
				url,
				method: 'POST',
				data: JSON.stringify({
					calendarPeriodId: periodId,
					series: seriesKey,
					proposedUpdates: approvedUpdates
				})
			})

			appDisplay.dialog(xhr, function(data) {
				return new DataUsedInCalculations({
					model: this.metric,
					seriesKey,
					series: this.series,
					periodId,
					data,
					result: deviceType.touchOnly ? $(e.target).data().value : $(e.target).text()
				})
			}.bind(this))
		}
	},

	autoRemove: true

})

/**
 * Returns the resource bundle key for a series. 0 returns
 * 'scorecards.node.metric.actualValue', and index 1 and higher return the
 * resource bundle keys for the thresholds.
 */
function getSeriesKey(scoringTypeId, index) {
	var seriesKey = 'scorecards.node.metric.actualValue'
	if (index > 0) {
		var scoringType = config.scoringType(scoringTypeId)
		seriesKey = scoringType.thresholdNames[index - 1]
	}
	return seriesKey
}

export default EquationValueTooltips
export { getSeriesKey }

EquationValueTooltips.__filename__ = 'scorecards/equation-value-tooltips.js'
